// M5.2: HTTP-клиент для gobot-license-server.
//
// Cookie-session (HttpOnly + SameSite=Lax) — поэтому withCredentials:true.
// Interceptor'ы:
//   - response: 401 → редирект на login + сброс auth store
//   - request: добавляем CSRF-токен из cookie в заголовок при mutating-методах

import axios, { type AxiosError, type AxiosInstance } from 'axios';

const BASE_URL = '/api';

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)')
  );
  return match && match[1] ? decodeURIComponent(match[1]) : null;
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// CSRF-токен сервер ставит в cookie `csrf_token` после login.
// Для mutating-методов (POST/PUT/DELETE/PATCH) дублируем в заголовок —
// классическая double-submit cookie защита от CSRF.
api.interceptors.request.use((config) => {
  const mutating = ['post', 'put', 'delete', 'patch'].includes((config.method ?? '').toLowerCase());
  if (mutating) {
    const token = readCookie('csrf_token');
    if (token) {
      config.headers['X-CSRF-Token'] = token;
    }
  }
  return config;
});

// Глобальная обработка ошибок:
//   401 unauthorized → редирект на /admin/login (зависит от текущего pathname).
//   429 rate-limit → toast (на уровне store).
api.interceptors.response.use(
  (resp) => resp,
  async (err: AxiosError<{ error?: string; message?: string }>) => {
    if (err.response?.status === 401) {
      // Динамический импорт, чтобы избежать цикла зависимостей
      // (store → api → store).
      const { useAuthStore } = await import('@/stores/auth');
      const auth = useAuthStore();
      auth.user = null;
      // Редирект только если мы не на login-странице (иначе loop).
      const path = window.location.pathname;
      if (!path.includes('/login')) {
        const target = path.startsWith('/cabinet') ? '/cabinet/login' : '/admin/login';
        const next = encodeURIComponent(path + window.location.search);
        window.location.href = `${target}?next=${next}`;
      }
    }
    return Promise.reject(err);
  }
);

// ---------- Доменные API ----------

export interface SessionUser {
  role: 'admin' | 'customer';
  email?: string;
  // Для customer'а — список доступных license_id'ов.
  licenses?: string[];
}

export interface License {
  ID: number;
  LicenseID: string;
  ActivationCode: string;
  IssuedTo: string;
  IssuedToEmail: string;
  IssuedAt: string;
  ValidUntil: string | null;
  MaxVersion: string;
  MinVersion: string;
  HeartbeatRequired: boolean;
  HeartbeatIntervalHours: number;
  GracePeriodHours: number;
  Features: string;
  MaxQuikClients: number; // MD/295: лимит одновременных QUIK-счетов (MD/265)
  HWID: string;
  ClientIDHash: string;
  ActivatedAt: string | null;
  ForceHeartbeatUntil: string | null;
  RevokedAt: string | null;
  RevokeReason: string;
  Notes: string;
  CreatedAt: string;
  UpdatedAt: string;
}

// MD/298: extended type для ответа /admin/licenses_page — добавлено поле
// LastHeartbeatVersion (последняя зарегистрированная версия клиента из
// heartbeats; null если ни одного heartbeat'а ещё не было). Backend
// возвращает License + это поле через correlated subquery; модель License
// остаётся 1:1 с DB-row.
export interface LicenseListItem extends License {
  LastHeartbeatVersion: string | null;
}

export interface Heartbeat {
  ID: number;
  LicenseID: string;
  HWID: string;
  Version: string;
  IP: string;
  UserAgent: string;
  ReceivedAt: string;
}

export interface DashboardStats {
  total: number;
  active: number;       // ActivatedAt != null && RevokedAt == null
  revoked: number;
  unactivated: number;
  heartbeats_24h: number;
  warning_count: number;  // > 24h без heartbeat'а
  readonly_count: number; // > 15 дней
}

// ---------- Session ----------

export async function loginAdmin(token: string): Promise<SessionUser> {
  // M5.1 (backend): POST /api/session/login {token} → 200 + Set-Cookie session.
  const { data } = await api.post<SessionUser>('/session/login', { token, role: 'admin' });
  return data;
}

export async function loginCustomerByLink(magicToken: string): Promise<SessionUser> {
  const { data } = await api.post<SessionUser>('/session/login', {
    token: magicToken,
    role: 'customer',
  });
  return data;
}

export async function requestCustomerLink(email: string): Promise<{ status: string }> {
  const { data } = await api.post<{ status: string }>('/session/customer/request', { email });
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/session/logout');
}

export async function getMe(): Promise<SessionUser | null> {
  try {
    const { data } = await api.get<SessionUser>('/session/me');
    return data;
  } catch (err) {
    if ((err as AxiosError).response?.status === 401) return null;
    throw err;
  }
}

// ---------- Admin domain ----------

export interface ListLicensesParams {
  status?: 'active' | 'revoked' | 'unactivated';
  email?: string;
  limit?: number;
}

export async function listLicenses(params: ListLicensesParams = {}): Promise<License[]> {
  const { data } = await api.get<{ items: License[]; count: number }>('/admin/licenses', { params });
  return data.items ?? [];
}

// M5.3: server-side pagination через /admin/licenses_page.
export interface ListLicensesPageParams {
  page?: number;
  per_page?: number;
  status?: 'active' | 'revoked' | 'unactivated';
  email?: string;
  sort?: 'issued_at' | 'issued_to_email' | 'max_version' | 'license_id' | 'activated_at' | 'revoked_at';
  desc?: boolean;
}

export interface ListLicensesPageResponse {
  items: LicenseListItem[]; // MD/298: расширенный тип с LastHeartbeatVersion
  total: number;
  page: number;
  per_page: number;
}

export async function listLicensesPage(params: ListLicensesPageParams = {}): Promise<ListLicensesPageResponse> {
  // axios сам сериализует boolean → 'true'/'false', а сервер ожидает '1'.
  const q: Record<string, string | number> = {};
  if (params.page) q.page = params.page;
  if (params.per_page) q.per_page = params.per_page;
  if (params.status) q.status = params.status;
  if (params.email) q.email = params.email;
  if (params.sort) q.sort = params.sort;
  if (params.desc) q.desc = '1';

  const { data } = await api.get<ListLicensesPageResponse>('/admin/licenses_page', { params: q });
  return data;
}

// M5.3: timeline heartbeat'ов для DashboardView Chart.js.
export interface TimelineBucket {
  bucket_start: number; // unix seconds
  count: number;
}

export interface TimelineResponse {
  status: string;
  period: '24h' | '7d' | '30d';
  generated: number;
  items: TimelineBucket[];
}

export async function getHeartbeatsTimeline(period: '24h' | '7d' | '30d' = '24h'): Promise<TimelineResponse> {
  const { data } = await api.get<TimelineResponse>('/admin/heartbeats/timeline', {
    params: { period },
  });
  return data;
}

// MD/309 — последний heartbeat per license за окно для таблицы
// "кто работает сейчас" на Dashboard. GORM Scan возвращает gorm-теги
// snake_case, поэтому поля snake_case (в отличие от Heartbeat выше,
// который сериализуется через стандартный JSON и даёт PascalCase).
export interface RecentHeartbeat {
  license_id: string;
  issued_to_email: string;
  hwid: string;
  version: string;
  ip: string;
  received_at: string;
  client_token: string; // непустой = защита MD/302 активна
  // MD/319 — Force-window для столбца на Dashboard.
  // null/undefined = классический grace 15 дней.
  // ISO timestamp > now = ускоренный режим (READONLY после 3ч разрыва).
  force_heartbeat_until?: string | null;
}

export interface RecentHeartbeatsResponse {
  status: string;
  window_hours: number;
  count: number;
  items: RecentHeartbeat[];
}

export async function getRecentHeartbeats(
  windowHours = 24,
  limit = 100,
): Promise<RecentHeartbeatsResponse> {
  const { data } = await api.get<RecentHeartbeatsResponse>('/admin/heartbeats/recent', {
    params: { window_hours: windowHours, limit },
  });
  return data;
}

// MD/321 — drill-down лицензий в WARNING/READONLY под счётчиком на Dashboard.
// status/hours_silent уже посчитаны backend'ом по per-license grace_period_hours.
export interface StaleLicense {
  id: number;
  license_id: string;
  issued_to_email: string;
  grace_period_hours: number;
  last_heartbeat_at: string | null; // null = heartbeat'а вообще не было
  last_version: string;
  hours_silent: number;
  force_heartbeat_until?: string | null;
  status: 'WARNING' | 'READONLY';
}

export interface StaleLicensesResponse {
  status: string;
  count: number;
  warning_count: number;
  readonly_count: number;
  items: StaleLicense[];
}

export async function getStaleLicenses(): Promise<StaleLicensesResponse> {
  const { data } = await api.get<StaleLicensesResponse>('/admin/licenses/stale');
  return data;
}

// ---------- Customer cabinet (M5.4) ----------

// Облегчённая JSON-форма с сервера (см. customer.go customerLicenseJSON).
// Поля времени — unix seconds.
export interface CustomerLicense {
  license_id: string;
  issued_to: string;
  issued_to_email: string;
  issued_at: number;
  valid_until?: number;
  max_version: string;
  min_version?: string;
  heartbeat_required: boolean;
  features?: string;
  activated_at?: number;
  revoked_at?: number;
  revoke_reason?: string;
  has_hwid: boolean;
}

export interface CustomerLicenseDetail {
  license: CustomerLicense;
  last_heartbeat_at?: number;
  last_heartbeat_version?: string;
}

export interface CustomerHeartbeat {
  received_at: number;
  version: string;
  hwid_short: string;
}

export async function listCustomerLicenses(): Promise<CustomerLicense[]> {
  const { data } = await api.get<{ items: CustomerLicense[] }>('/customer/licenses');
  return data.items ?? [];
}

export async function getCustomerLicense(licenseID: string): Promise<CustomerLicenseDetail> {
  const { data } = await api.get<{ license: CustomerLicense; last_heartbeat_at?: number; last_heartbeat_version?: string }>(
    `/customer/license/${encodeURIComponent(licenseID)}`
  );
  return {
    license: data.license,
    last_heartbeat_at: data.last_heartbeat_at,
    last_heartbeat_version: data.last_heartbeat_version,
  };
}

export async function listCustomerHeartbeats(licenseID: string, limit = 100): Promise<CustomerHeartbeat[]> {
  const { data } = await api.get<{ items: CustomerHeartbeat[] }>(
    `/customer/license/${encodeURIComponent(licenseID)}/heartbeats`,
    { params: { limit } }
  );
  return data.items ?? [];
}

// downloadCustomerLicense — триггерит браузерный download. Возвращает
// при ошибке текст для отображения; при успехе ничего не возвращает
// (файл уже сохранён).
export async function downloadCustomerLicense(licenseID: string): Promise<void> {
  const resp = await api.get(`/customer/license/${encodeURIComponent(licenseID)}/download`, {
    responseType: 'blob',
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'license.lic';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Освобождаем blob — браузер сам очистит через ~5 сек, но явный
  // revoke надёжнее.
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

export async function requestCustomerTransfer(licenseID: string, reason: string): Promise<{ status: string; hint?: string }> {
  const { data } = await api.post<{ status: string; hint?: string }>(
    `/customer/license/${encodeURIComponent(licenseID)}/transfer_request`,
    { reason }
  );
  return data;
}

// Resend pre-activation .lic to customer's email. Сервер шлёт письмо
// с attachment license.lic + пошаговой инструкцией. На бэке стоит
// cooldown (~5 мин между переотправками) — при срабатывании ответ 429.
export async function emailCustomerLicense(licenseID: string): Promise<{ status: string; email?: string }> {
  const { data } = await api.post<{ status: string; email?: string }>(
    `/customer/license/${encodeURIComponent(licenseID)}/send_email`
  );
  return data;
}

export async function getLicense(licenseID: string): Promise<{ license: License; last_heartbeat: Heartbeat | null }> {
  const { data } = await api.get<{ license: License; last_heartbeat: Heartbeat | null }>(
    `/admin/license/${encodeURIComponent(licenseID)}`
  );
  return data;
}

export async function listHeartbeats(licenseID: string, limit = 100): Promise<Heartbeat[]> {
  const { data } = await api.get<{ items: Heartbeat[] }>(
    `/admin/license/${encodeURIComponent(licenseID)}/heartbeats`,
    { params: { limit } }
  );
  return data.items ?? [];
}

export interface IssueLicenseRequest {
  issued_to_email: string;
  issued_to?: string;
  valid_until?: string; // YYYY-MM-DD
  max_version?: string;
  min_version?: string;
  heartbeat_interval_hours?: number;
  grace_period_hours?: number;
  max_quik_clients?: number; // MD/265: default 3 если не указано
  features?: string[];
  notes?: string;
  // MD/265: heartbeat_required убрано — больше не настраивается, всегда вкл,
  // но grace бесконечный (никогда не BAN из-за оффлайна).
}

export interface IssueLicenseResponse {
  status: string;
  license_id: string;
  activation_code: string;
  issued_to_email: string;
}

export async function issueLicense(req: IssueLicenseRequest): Promise<IssueLicenseResponse> {
  const { data } = await api.post<IssueLicenseResponse>('/admin/issue', req);
  return data;
}

// MD/265 — скачать готовый .lic файл (pre-activation envelope для не
// активированных + финальный .lic для уже активированных). Через axios
// с responseType:'blob', чтобы браузер показал save-dialog.
export async function downloadLicenseFile(licenseID: string): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `/admin/license/${encodeURIComponent(licenseID)}/download.lic`,
    { responseType: 'blob' }
  );
  return data;
}

export async function revokeLicense(licenseID: string, reason: string): Promise<void> {
  await api.post('/admin/revoke', { license_id: licenseID, reason });
}

// MD/299 — исправление email лицензии (опечатка / смена адреса).
// IssuedToEmail не покрыт HMAC integrity, поэтому правка безопасна и не
// требует пересборки .lic у клиента. После update вызови emailAdminLicense
// чтобы тут же переотправить .lic на новый адрес.
//
// Серверные ответы:
//   200 → {status, license_id, old_email, new_email, noop?}
//   400 bad_request — пустой/невалидный email
//   404 not_found    — лицензия не существует
export async function updateLicenseEmail(licenseID: string, email: string): Promise<{
  status: string;
  license_id: string;
  old_email: string;
  new_email: string;
  noop?: boolean;
}> {
  const { data } = await api.patch(
    `/admin/license/${encodeURIComponent(licenseID)}/email`,
    { email },
  );
  return data;
}

// MD/286 расширение — переотправка .lic покупателю с адреса лицензии
// (support-сценарий «клиент потерял письмо»). Backend cooldown 30 секунд,
// при срабатывании → 429. При отсутствии email на лицензии → 422.
// При не настроенном SMTP → 503.
export async function emailAdminLicense(licenseID: string): Promise<{ status: string; email?: string }> {
  const { data } = await api.post<{ status: string; email?: string }>(
    `/admin/license/${encodeURIComponent(licenseID)}/send_email`
  );
  return data;
}

// Список релизов, доступных к рассылке. Backend сканирует
// /opt/gobot/releases/*/gobot-*.zip и возвращает версии (+ размер,
// опционально sha256). Используется диалогом «Отправить версию»
// в карточке лицензии.
export interface ReleaseEntry {
  version: string;
  size: number;
  sha256?: string;
}

export async function listAdminReleases(): Promise<ReleaseEntry[]> {
  const { data } = await api.get<{ releases: ReleaseEntry[] }>('/admin/releases');
  return data.releases ?? [];
}

// Отправка ссылки на скачивание конкретной версии письмом покупателю.
// Сервер генерит одноразовый HMAC-token (TTL 7 дней) и шлёт plain-text
// письмо со ссылкой. Cooldown 1 минута; при срабатывании → 429.
// При отсутствии версии в /opt/gobot/releases → 404, отсутствии email
// на лицензии → 422, не настроенном SMTP → 503, отозванной лицензии → 410.
export async function sendAdminVersion(
  licenseID: string,
  version: string
): Promise<{ status: string; email?: string }> {
  const { data } = await api.post<{ status: string; email?: string }>(
    `/admin/license/${encodeURIComponent(licenseID)}/send_version`,
    { version }
  );
  return data;
}

export async function transferLicense(licenseID: string, forceWindowHours = 168): Promise<{
  new_activation_code: string;
  force_window_until: number;
}> {
  const { data } = await api.post<{
    status: string;
    license_id: string;
    new_activation_code: string;
    force_window_until: number;
  }>('/admin/transfer', { license_id: licenseID, force_window_hours: forceWindowHours });
  return data;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  const { data } = await api.get<DashboardStats>('/admin/dashboard');
  return data;
}
